import React, { createContext, useState, useContext, useCallback } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import Grow from '@mui/material/Grow';

const SnackbarContext = createContext(null);

const DEFAULT_OPTIONS = {
    action: false,
    open: false,
    message: 'Default message',
    anchorOrigin: {
        vertical: 'top',
        horizontal: 'center'
    },
    variant: 'default',
    alert: {
        color: 'primary',
        variant: 'outlined'
    },
    transition: 'Fade',
    close: false,
    actionButton: false,
    maxStack: 3,
    dense: false,
    iconVariant: 'usedefault',
    isGuardAlert: false,
    autoHideDuration: 3000
};

const transitions = {
    Fade: Fade,
    Slide: Slide,
    Grow: Grow
};

export const SnackbarProvider = ({ children }) => {
    const [snackbarState, setSnackbarState] = useState(DEFAULT_OPTIONS);

    const showSnackbar = useCallback((message, options = {}) => {
        const merged = {
            ...DEFAULT_OPTIONS,
            ...options,
            open: true,
            message,
            variant: options.variant || 'default'
        };
        setSnackbarState(merged);
    }, []);

    const handleClose = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarState(prev => ({ ...prev, open: false }));
    };

    const TransitionComponent = transitions[snackbarState.transition] || Fade;

    return (
        <SnackbarContext.Provider value={showSnackbar}>
            {children}
            <Snackbar
                open={snackbarState.open}
                onClose={handleClose}
                autoHideDuration={snackbarState.autoHideDuration}
                anchorOrigin={snackbarState.anchorOrigin}
                TransitionComponent={TransitionComponent}
            >
                <Alert
                    onClose={snackbarState.close ? handleClose : undefined}
                    severity={snackbarState.variant}
                    variant={snackbarState.alert.variant}
                    color={snackbarState.alert.color}
                    sx={
                        snackbarState.alert.variant === 'outlined'
                            ? { backgroundColor: '#fff' }
                            : {}
                    }
                >
                    {snackbarState.message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = () => useContext(SnackbarContext);
